import { t } from 'i18next'
import ScheduleTableDropdown from '../../views/ScheduleView/ScheduleTable.tsx/ScheduleTableDropdown'
import ScheduleTableError from '../../views/ScheduleView/ScheduleTable.tsx/ScheduleTableError'
import { Cell, HeaderCell, HoursInterval, ScheduleTableVariant, WorkDay } from '../types'
import { formatHours, getVacationId, sumAllPerDay, sumProjectPerDay } from './scheduleSummaryUtils'

export const getHeaderCells = (variant: ScheduleTableVariant) => {
  const headerCells: Record<ScheduleTableVariant, HeaderCell[]> = {
    planned: [
      {
        id: `planned-table-header-date-cell`,
        title: t('components.table.cell.date'),
        align: 'left',
        width: '20%',
      },
      {
        id: 'planned-table-header-from-cell',
        title: t('components.table.cell.from'),
        align: 'center',
        width: '25%',
      },
      {
        id: 'planned-table-header-to-cell',
        title: t('components.table.cell.to'),
        align: 'center',
        width: '25%',
      },
      {
        id: 'planned-table-header-time-cell',
        title: t('components.table.cell.time'),
        align: 'center',
        width: '25%',
      },
      {
        id: 'planned-table-header-dropdown-cell',
        title: '',
        align: 'center',
        width: '5%',
      },
    ],
    tracked: [
      {
        id: 'tracked-table-header-date-cell',
        title: t('components.table.cell.date'),
        align: 'left',
        width: '17%',
      },
      {
        id: 'tracked-table-header-from-cell',
        title: t('components.table.cell.from'),
        align: 'center',
        width: '9%',
      },
      {
        id: 'tracked-table-header-to-cell',
        title: t('components.table.cell.to'),
        align: 'center',
        width: '9%',
      },
      {
        id: 'tracked-table-header-tracked-cell',
        title: t('components.table.cell.tracked'),
        align: 'center',
        width: '30%',
      },
      {
        id: 'tracked-table-header-registered-cell',
        title: t('components.table.cell.registered'),
        fullTitle: t('components.table.cell.registeredFull'),
        align: 'center',
        width: '30%',
      },
      {
        id: 'tracked-table-header-error-cell',
        title: '',
        align: 'center',
        width: '5%',
      },
    ],
    summary: [
      {
        id: 'summary-table-header-date-cell',
        title: t('components.table.cell.date'),
        align: 'left',
        width: '20%',
      },
      {
        id: 'summary-table-header-work-time-cell',
        title: t('components.table.cell.workTime'),
        align: 'center',
        width: '30%',
      },
      {
        id: 'summary-table-header-vacation-cell',
        title: t('components.table.cell.vacation'),
        align: 'center',
        width: '20%',
      },
      {
        id: 'summary-table-header-overtime-cell',
        title: t('components.table.cell.overtime'),
        align: 'center',
        width: '30%',
      },
    ],
  }

  return headerCells[variant]
}

export const getPlannedCells = (date: string, intervals: HoursInterval[], allHours: string): Cell[] => {
  return [
    {
      id: `${date}-planned-from-cell`,
      value: intervals ? intervals[0].from : '',
      colspan: 1,
      align: 'center',
    },
    {
      id: `${date}-planned-to-cell`,
      value: intervals ? intervals[intervals.length - 1].to : '',
      colspan: 1,
      align: 'center',
    },
    {
      id: `${date}-planned-all-hours-cell`,
      value: allHours,
      colspan: 1,
      align: 'center',
    },
    {
      id: `${date}-planned-hover-cell`,
      value: <ScheduleTableDropdown />,
      colspan: 1,
      align: 'center',
      className: 'hoverable-row',
    },
  ]
}

export const getTrackedCells = (
  date: string,
  intervals: HoursInterval[],
  allHours: string,
  isSelected: boolean,
  isBadSchedule: boolean,
  workHours: string
): Cell[] => {
  return [
    {
      id: `${date}-tracked-from-cell`,
      value: intervals ? intervals[0].from : '',
      colspan: 1,
      align: 'center',
    },
    {
      id: `${date}-tracked-to-cell`,
      value: intervals ? intervals[intervals.length - 1].to : '',
      colspan: 1,
      align: 'center',
    },
    {
      id: `${date}-tracked-hours-cell`,
      value: allHours,
      colspan: 1,
      align: 'center',
    },
    {
      id: `${date}-tracked-registered-hours-cell`,
      value: workHours,
      colspan: 1,
      align: 'center',
    },
    {
      id: `${date}-tracked-error-cell`,
      value: isBadSchedule ? <ScheduleTableError isSelected={isSelected} /> : <ScheduleTableDropdown />,
      colspan: 1,
      align: 'center',
      className: 'hoverable-row',
    },
  ]
}

export const getSummaryCells = (date: string, worklog: WorkDay | undefined): Cell[] => {
  let todaysTotal = sumAllPerDay(worklog, date)
  const vacationId = getVacationId(worklog)
  const vacationTotal = sumProjectPerDay(worklog, vacationId, date)
  let overtimeTotal = 0
  if (todaysTotal > 8) {
    overtimeTotal = todaysTotal - 8
    todaysTotal = 8 - vacationTotal
  } else todaysTotal = todaysTotal - vacationTotal
  if (todaysTotal < 0) todaysTotal = 0
  return [
    {
      id: `${date}-summary-work-time-cell`,
      value: todaysTotal === 0 ? '-' : formatHours(todaysTotal),
      align: 'center',
    },
    {
      id: `${date}-summary-vacation-cell`,
      value: vacationTotal === 0 ? '-' : formatHours(vacationTotal),
      align: 'center',
    },
    {
      id: `${date}-summary-overtime-cell`,
      value: overtimeTotal === 0 ? '-' : `+${formatHours(overtimeTotal)}`,
      align: 'center',
    },
  ]
}
